/**
 * Страница настройки Telegram Client (MTProto)
 * Дата: 30.12.2025
 * Назначение: Страница для подключения и управления Telegram Client аккаунтом
 * Обновлено: 30.12.2025 - Заменен alert на toast, получение bot name из API
 * Обновлено: 29.12.2025 - Добавлен TenantSelector для SuperAdmin
 * Дата: 26.01.2026 - Добавлено frontend логирование (TODO-034)
 * Дата: 26.01.2026 - Добавлена авторизация по телефону (TODO-035: Итерации 1-3)
 *   - Итерация 1-2: Backend и Frontend API, UI с табами и формами
 *   - Итерация 3: Обработка edge cases (2FA, flood wait, истёкший код, ошибки)
 * 
 * Связанные модули:
 * - frontend/src/api/telegramClient.ts - API методы для phone auth
 * - internal/handler/telegram_client.go - HTTP handlers
 * - internal/domain/service/telegram/client_service.go - бизнес-логика
 * - internal/adapter/messenger/telegram/client.go - адаптер для gotd API
 */
import { useState, useEffect } from 'react';
import { logger } from '@/utils/logger';
import { MainLayout } from '@/components/layout/MainLayout';
import { Card, CardHeader, CardTitle, CardContent, Button, TenantSelector, Input } from '@/components/ui';
import { TelegramClientStatus } from '@/components/TelegramClientStatus';
import { TelegramQRAuth } from '@/components/TelegramQRAuth';
import { checkAuthStatus, type AuthStatus, sendCode, verifyCode, verifyPassword, type PhoneAuthError, type VerifyCodeResponse } from '@/api/telegramClient';
import { botApi } from '@/api/bot';
import { useTenantScope } from '@/hooks/useTenantScope';
import { useToast } from '@/hooks/useToast';
import { Smartphone, Search, MessageSquare, Building2 } from 'lucide-react';

export function TelegramClientSetupPage() {
  const [authTab, setAuthTab] = useState<'qr' | 'phone'>('qr'); // Дата: 26.01.2026 - TODO-035
  const [showQRAuth, setShowQRAuth] = useState(false);
  const [authStatus, setAuthStatus] = useState<AuthStatus | null>(null);
  const [botName, setBotName] = useState<string>('@dhc_clinic_bot'); // Fallback значение
  
  // Phone auth state (Дата: 26.01.2026 - TODO-035)
  const [phone, setPhone] = useState('');
  const [code, setCode] = useState('');
  const [codeHash, setCodeHash] = useState('');
  const [phoneAuthStep, setPhoneAuthStep] = useState<'phone' | 'code' | '2fa'>('phone'); // Дата: 26.01.2026 - TODO-035: Итерация 3
  const [isSendingCode, setIsSendingCode] = useState(false);
  const [isVerifyingCode, setIsVerifyingCode] = useState(false);
  const [isVerifyingPassword, setIsVerifyingPassword] = useState(false);
  const [resendTimer, setResendTimer] = useState(0);
  const [floodWaitTimer, setFloodWaitTimer] = useState(0); // Дата: 26.01.2026 - TODO-035: Итерация 3
  const [passwordHint, setPasswordHint] = useState(''); // Дата: 26.01.2026 - TODO-035: Итерация 3
  const [password2FA, setPassword2FA] = useState(''); // Дата: 26.01.2026 - TODO-035: Итерация 3
  const [authError, setAuthError] = useState<PhoneAuthError | null>(null); // Дата: 26.01.2026 - TODO-035: Итерация 3
  
  const { showWarning, showSuccess, showError } = useToast();
  const { tenantId, isSuperAdmin, selectedTenantId, setSelectedTenant, requireTenantSelection } = useTenantScope();
  const effectiveTenantId = tenantId || '';

  // Логирование просмотра страницы
  useEffect(() => {
    logger.info('page_view', { page: 'telegram-client' });
  }, []);

  // Загрузить имя бота из настроек
  useEffect(() => {
    const loadBotSettings = async () => {
      // Для SuperAdmin требуется выбранный tenant
      if (isSuperAdmin && !effectiveTenantId) {
        return;
      }
      try {
        const settings = await botApi.getSettings(effectiveTenantId);
        if (settings?.bot_username) {
          setBotName(`@${settings.bot_username}`);
          logger.info('telegram_client_bot_settings_loaded', { 
            bot_username: settings.bot_username,
            tenant_id: effectiveTenantId 
          });
        } else {
          setBotName('Не настроен');
        }
      } catch (err) {
        logger.error('telegram_client_bot_settings_load_failed', { 
          error: err instanceof Error ? err.message : 'Unknown error',
          tenant_id: effectiveTenantId 
        });
        setBotName('Ошибка загрузки');
      }
    };
    loadBotSettings();
  }, [effectiveTenantId, isSuperAdmin]);

  const handleStartAuth = async () => {
    logger.info('telegram_client_auth_start_click', { tenant_id: effectiveTenantId });
    // Проверить текущий статус
    try {
      const status = await checkAuthStatus(effectiveTenantId);
      logger.info('telegram_client_status_check', { 
        status: status.status,
        tenant_id: effectiveTenantId 
      });
      if (status.status === 'connected') {
        logger.warning('telegram_client_already_connected', { tenant_id: effectiveTenantId });
        showWarning('Telegram аккаунт уже подключен');
        return;
      }
      setShowQRAuth(true);
      logger.info('telegram_client_qr_auth_started', { tenant_id: effectiveTenantId });
    } catch (err) {
      logger.error('telegram_client_status_check_failed', { 
        error: err instanceof Error ? err.message : 'Unknown error',
        tenant_id: effectiveTenantId 
      });
      setShowQRAuth(true);
    }
  };

  const handleAuthSuccess = (status: AuthStatus) => {
    logger.info('telegram_client_auth_success', { 
      status: status.status,
      tenant_id: effectiveTenantId 
    });
    setShowQRAuth(false);
    setAuthStatus(status);
    // Перезагрузить страницу для обновления статуса
    window.location.reload();
  };

  const handleAuthCancel = () => {
    logger.info('telegram_client_auth_cancelled', { tenant_id: effectiveTenantId });
    setShowQRAuth(false);
  };

  const handleDisconnect = () => {
    logger.info('telegram_client_disconnect_click', { tenant_id: effectiveTenantId });
    setAuthStatus({ status: 'disconnected' });
  };

  // Phone auth handlers (Дата: 26.01.2026 - TODO-035)
  const handleSendCode = async () => {
    if (!phone.trim()) {
      showWarning('Введите номер телефона');
      return;
    }

    // Проверить flood wait таймер
    if (floodWaitTimer > 0) {
      showWarning(`Подождите ${floodWaitTimer} секунд перед повторной отправкой`);
      return;
    }

    logger.info('telegram_client_phone_auth_send_code_click', { 
      tenant_id: effectiveTenantId,
      phone_length: phone.length 
    });
    setIsSendingCode(true);
    setAuthError(null); // Дата: 26.01.2026 - TODO-035: Итерация 3 - очистка ошибок
    try {
      const result = await sendCode(phone, effectiveTenantId);
      setCodeHash(result.code_hash);
      setPhoneAuthStep('code');
      setResendTimer(60); // 60 секунд таймер
      logger.info('telegram_client_phone_auth_code_sent', { 
        tenant_id: effectiveTenantId 
      });
      showSuccess('Код отправлен в Telegram');
      
      // Таймер обратного отсчёта
      const interval = setInterval(() => {
        setResendTimer((prev) => {
          if (prev <= 1) {
            clearInterval(interval);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    } catch (err: any) {
      // Дата: 26.01.2026 - TODO-035: Итерация 3 - обработка edge cases
      if (err.type === 'flood_wait') {
        const waitSeconds = err.wait_seconds || 60;
        setFloodWaitTimer(waitSeconds);
        setAuthError(err);
        logger.warning('telegram_client_phone_auth_flood_wait', {
          wait_seconds: waitSeconds,
          tenant_id: effectiveTenantId
        });
        showWarning(`Слишком много запросов. Подождите ${waitSeconds} секунд`);
        
        // Таймер flood wait
        const interval = setInterval(() => {
          setFloodWaitTimer((prev) => {
            if (prev <= 1) {
              clearInterval(interval);
              return 0;
            }
            return prev - 1;
          });
        }, 1000);
      } else if (err.type === 'session_exists') {
        setAuthError(err);
        logger.warning('telegram_client_phone_auth_session_exists', {
          tenant_id: effectiveTenantId
        });
        showWarning('Номер телефона уже используется другой сессией');
      } else {
        // Дата: 26.01.2026 - TODO-035: Итерация 3 - обработка обычных ошибок
        const errorMessage = err.message || err.response?.data?.error || 'Ошибка отправки кода';
        logger.error('telegram_client_phone_auth_send_code_failed', {
          error: errorMessage,
          error_type: err.type || 'unknown',
          tenant_id: effectiveTenantId
        });
        // Установить authError даже для обычных ошибок (для отображения в UI)
        setAuthError({
          type: err.type || 'unknown',
          message: errorMessage
        } as PhoneAuthError);
        showError(errorMessage);
      }
    } finally {
      setIsSendingCode(false);
    }
  };

  const handleVerifyCode = async () => {
    if (!code.trim()) {
      showWarning('Введите код');
      return;
    }

    logger.info('telegram_client_phone_auth_verify_code_click', { 
      tenant_id: effectiveTenantId 
    });
    setIsVerifyingCode(true);
    setAuthError(null); // Дата: 26.01.2026 - TODO-035: Итерация 3 - очистка ошибок
    try {
      const result: VerifyCodeResponse = await verifyCode(phone, code, codeHash, effectiveTenantId);
      
      // Дата: 26.01.2026 - TODO-035: Итерация 3 - проверка 2FA
      if (result.requires_2fa) {
        setPasswordHint(result.password_hint || '');
        setPhoneAuthStep('2fa');
        logger.info('telegram_client_phone_auth_2fa_required', { 
          tenant_id: effectiveTenantId,
          has_hint: !!result.password_hint
        });
        showWarning('Требуется пароль двухфакторной аутентификации');
      } else if (result.success) {
        logger.info('telegram_client_phone_auth_verified', { 
          tenant_id: effectiveTenantId,
          user_id: result.user_id 
        });
        showSuccess('Авторизация успешна!');
        // Перезагрузить страницу для обновления статуса
        window.location.reload();
      } else {
        showError('Неверный код');
      }
    } catch (err: any) {
      // Дата: 26.01.2026 - TODO-035: Итерация 3 - обработка edge cases
      if (err.type === 'code_expired') {
        setAuthError(err);
        logger.warning('telegram_client_phone_auth_code_expired', {
          tenant_id: effectiveTenantId
        });
        showWarning('Код истёк. Запросите новый код');
        setPhoneAuthStep('phone');
        setCode('');
        setCodeHash('');
      } else if (err.type === 'invalid_code') {
        setAuthError(err);
        logger.warning('telegram_client_phone_auth_invalid_code', {
          tenant_id: effectiveTenantId
        });
        showError('Неверный код. Попробуйте ещё раз');
      } else {
        // Дата: 26.01.2026 - TODO-035: Итерация 3 - обработка обычных ошибок
        const errorMessage = err.message || err.response?.data?.error || 'Ошибка верификации кода';
        logger.error('telegram_client_phone_auth_verify_code_failed', {
          error: errorMessage,
          error_type: err.type || 'unknown',
          tenant_id: effectiveTenantId
        });
        // Установить authError даже для обычных ошибок (для отображения в UI)
        setAuthError({
          type: err.type || 'unknown',
          message: errorMessage
        } as PhoneAuthError);
        showError(errorMessage);
      }
    } finally {
      setIsVerifyingCode(false);
    }
  };

  // Дата: 26.01.2026 - TODO-035: Итерация 3 - обработка 2FA
  const handleVerifyPassword = async () => {
    if (!password2FA.trim()) {
      showWarning('Введите пароль');
      return;
    }

    logger.info('telegram_client_2fa_verify_password_click', { 
      tenant_id: effectiveTenantId 
    });
    setIsVerifyingPassword(true);
    setAuthError(null);
    try {
      const result = await verifyPassword(phone, password2FA, effectiveTenantId);
      if (result.success) {
        logger.info('telegram_client_2fa_verified', { 
          tenant_id: effectiveTenantId,
          user_id: result.user_id 
        });
        showSuccess('Авторизация успешна!');
        // Перезагрузить страницу для обновления статуса
        window.location.reload();
      } else {
        showError('Неверный пароль');
      }
    } catch (err: any) {
      // Дата: 26.01.2026 - TODO-035: Итерация 3 - обработка ошибок 2FA
      const errorMessage = err.message || err.response?.data?.error || 'Ошибка верификации пароля';
      logger.error('telegram_client_2fa_verify_password_failed', {
        error: errorMessage,
        error_type: err.type || 'unknown',
        tenant_id: effectiveTenantId
      });
      // Установить authError даже для обычных ошибок (для отображения в UI)
      setAuthError({
        type: err.type || 'unknown',
        message: errorMessage
      } as PhoneAuthError);
      showError(errorMessage);
    } finally {
      setIsVerifyingPassword(false);
    }
  };

  const handleResendCode = () => {
    setCode('');
    setCodeHash('');
    setPhoneAuthStep('phone');
    setAuthError(null);
    handleSendCode();
  };
  
  const handleBackToPhone = () => {
    setPhoneAuthStep('phone');
    setCode('');
    setCodeHash('');
    setPassword2FA('');
    setPasswordHint('');
    setAuthError(null);
  };
  
  const handleBackToCode = () => {
    setPhoneAuthStep('code');
    setPassword2FA('');
    setAuthError(null);
  };

  return (
    <MainLayout headerTitle="Настройки Telegram">
      <div className="p-6 space-y-6">
        {/* TenantSelector для SuperAdmin */}
        {isSuperAdmin && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Building2 className="w-5 h-5" />
                Выбор клиники
              </CardTitle>
            </CardHeader>
            <CardContent>
              <TenantSelector 
                value={selectedTenantId || ''} 
                onChange={setSelectedTenant} 
              />
              {requireTenantSelection && (
                <p className="text-sm text-amber-600 mt-2">
                  ⚠️ Выберите клинику для настройки Telegram Client
                </p>
              )}
            </CardContent>
          </Card>
        )}

        {/* Telegram Bot секция */}
        <Card>
          <CardHeader>
            <CardTitle>TELEGRAM BOT</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-neutral-600">
                  ✅ Подключен: {botName}
                </p>
                <p className="text-xs text-neutral-500 mt-1">
                  Для рассылок и приёма сообщений через бота
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Telegram Client секция */}
        <Card>
          <CardHeader>
            <CardTitle>TELEGRAM АККАУНТ (Client)</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Для SuperAdmin: требуется выбор тенанта */}
            {requireTenantSelection ? (
              <div className="text-center py-8 text-neutral-500">
                <Building2 className="w-12 h-12 mx-auto mb-3 opacity-50" />
                <p>Выберите клинику выше для настройки Telegram Client</p>
              </div>
            ) : !showQRAuth ? (
              <>
                <TelegramClientStatus onDisconnect={handleDisconnect} tenantId={effectiveTenantId} />
                {(authStatus?.status === 'disconnected' || authStatus?.status === 'auth_required' || !authStatus) && (
                  <div className="pt-4 border-t border-neutral-200">
                    <div className="space-y-3 mb-4">
                      <p className="text-sm text-neutral-700 font-medium">
                        Подключите Telegram аккаунт клиники для:
                      </p>
                      <ul className="space-y-2 text-sm text-neutral-600">
                        <li className="flex items-start gap-2">
                          <Search className="w-4 h-4 mt-0.5 text-primary-600" />
                          <span>Поиска пациентов по номеру телефона</span>
                        </li>
                        <li className="flex items-start gap-2">
                          <MessageSquare className="w-4 h-4 mt-0.5 text-primary-600" />
                          <span>Переписки как обычный аккаунт (не бот)</span>
                        </li>
                        <li className="flex items-start gap-2">
                          <Smartphone className="w-4 h-4 mt-0.5 text-primary-600" />
                          <span>Просмотра всех диалогов аккаунта</span>
                        </li>
                      </ul>
                    </div>
                    
                    {/* Табы выбора метода авторизации (Дата: 26.01.2026 - TODO-035) */}
                    <div className="mb-4">
                      <div className="flex gap-2 border-b border-neutral-200">
                        <button
                          onClick={() => {
                            setAuthTab('qr');
                            setShowQRAuth(false);
                            setPhoneAuthStep('phone');
                            setPhone('');
                            setCode('');
                            setCodeHash('');
                          }}
                          className={`px-4 py-2 text-sm font-medium transition-colors ${
                            authTab === 'qr'
                              ? 'border-b-2 border-primary-600 text-primary-600'
                              : 'text-neutral-500 hover:text-neutral-700'
                          }`}
                        >
                          QR-код
                        </button>
                        <button
                          onClick={() => {
                            setAuthTab('phone');
                            setShowQRAuth(false);
                            setPhoneAuthStep('phone');
                          }}
                          className={`px-4 py-2 text-sm font-medium transition-colors ${
                            authTab === 'phone'
                              ? 'border-b-2 border-primary-600 text-primary-600'
                              : 'text-neutral-500 hover:text-neutral-700'
                          }`}
                        >
                          По номеру телефона
                        </button>
                      </div>
                    </div>

                    {/* QR Auth */}
                    {authTab === 'qr' && (
                      <Button onClick={handleStartAuth} variant="primary" className="w-full">
                        📱 Подключить через QR-код
                      </Button>
                    )}

                    {/* Phone Auth (Дата: 26.01.2026 - TODO-035) */}
                    {authTab === 'phone' && (
                      <div className="space-y-4">
                        {/* Шаг 1: Ввод телефона */}
                        {phoneAuthStep === 'phone' ? (
                          <>
                            <div>
                              <label className="block text-sm font-medium text-neutral-700 mb-2">
                                Номер телефона
                              </label>
                              <Input
                                type="tel"
                                value={phone}
                                onChange={(e) => setPhone(e.target.value)}
                                placeholder="+79261234567"
                                disabled={isSendingCode || floodWaitTimer > 0}
                              />
                              <p className="text-xs text-neutral-500 mt-1">
                                Введите номер в международном формате
                              </p>
                              {/* Дата: 26.01.2026 - TODO-035: Итерация 3 - отображение flood wait */}
                              {floodWaitTimer > 0 && (
                                <p className="text-xs text-amber-600 mt-1">
                                  ⏳ Подождите {floodWaitTimer} секунд перед повторной отправкой
                                </p>
                              )}
                              {/* Дата: 26.01.2026 - TODO-035: Итерация 3 - отображение ошибок */}
                              {authError && authError.type === 'session_exists' && (
                                <p className="text-xs text-amber-600 mt-1">
                                  ⚠️ {authError.message}
                                </p>
                              )}
                              {authError && authError.type !== 'session_exists' && authError.type !== 'flood_wait' && (
                                <p className="text-xs text-red-600 mt-1">
                                  ❌ {authError.message}
                                </p>
                              )}
                            </div>
                            <Button
                              onClick={handleSendCode}
                              disabled={isSendingCode || !phone.trim() || floodWaitTimer > 0}
                              variant="primary"
                              className="w-full"
                            >
                              {isSendingCode ? 'Отправка...' : floodWaitTimer > 0 ? `Подождите ${floodWaitTimer}с` : 'Отправить код'}
                            </Button>
                          </>
                        ) : phoneAuthStep === 'code' ? (
                          /* Шаг 2: Ввод кода */
                          <>
                            <div>
                              <label className="block text-sm font-medium text-neutral-700 mb-2">
                                Код из Telegram
                              </label>
                              <Input
                                type="text"
                                value={code}
                                onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
                                placeholder="12345"
                                maxLength={5}
                                disabled={isVerifyingCode}
                              />
                              <p className="text-xs text-neutral-500 mt-1">
                                Код отправлен в Telegram на номер {phone}
                              </p>
                              {/* Дата: 26.01.2026 - TODO-035: Итерация 3 - отображение ошибок */}
                              {authError && (
                                <p className={`text-xs mt-1 ${
                                  authError.type === 'code_expired' ? 'text-amber-600' :
                                  authError.type === 'invalid_code' ? 'text-red-600' :
                                  'text-red-600'
                                }`}>
                                  {authError.type === 'code_expired' && '⏰ '}
                                  {authError.type === 'invalid_code' && '❌ '}
                                  {authError.message}
                                </p>
                              )}
                            </div>
                            <div className="flex gap-2">
                              <Button
                                onClick={handleVerifyCode}
                                disabled={isVerifyingCode || !code.trim() || code.length !== 5}
                                variant="primary"
                                className="flex-1"
                              >
                                {isVerifyingCode ? 'Проверка...' : 'Подтвердить'}
                              </Button>
                              {resendTimer > 0 ? (
                                <Button variant="secondary" disabled>
                                  Повторить ({resendTimer}с)
                                </Button>
                              ) : (
                                <Button variant="secondary" onClick={handleResendCode}>
                                  Повторить
                                </Button>
                              )}
                            </div>
                            <Button
                              onClick={handleBackToPhone}
                              variant="secondary"
                              className="w-full"
                            >
                              Изменить номер
                            </Button>
                          </>
                        ) : (
                          /* Шаг 3: Ввод пароля 2FA */
                          <>
                            <div>
                              <label className="block text-sm font-medium text-neutral-700 mb-2">
                                Пароль двухфакторной аутентификации
                              </label>
                              <Input
                                type="password"
                                value={password2FA}
                                onChange={(e) => setPassword2FA(e.target.value)}
                                placeholder="Введите пароль 2FA"
                                disabled={isVerifyingPassword}
                              />
                              {passwordHint && (
                                <p className="text-xs text-neutral-500 mt-1">
                                  💡 Подсказка: {passwordHint}
                                </p>
                              )}
                              <p className="text-xs text-neutral-500 mt-1">
                                Введите пароль, который вы установили для защиты аккаунта
                              </p>
                              {/* Дата: 26.01.2026 - TODO-035: Итерация 3 - отображение ошибок */}
                              {authError && (
                                <p className="text-xs text-red-600 mt-1">
                                  ❌ {authError.message}
                                </p>
                              )}
                            </div>
                            <div className="flex gap-2">
                              <Button
                                onClick={handleVerifyPassword}
                                disabled={isVerifyingPassword || !password2FA.trim()}
                                variant="primary"
                                className="flex-1"
                              >
                                {isVerifyingPassword ? 'Проверка...' : 'Подтвердить'}
                              </Button>
                              <Button
                                onClick={handleBackToCode}
                                variant="secondary"
                              >
                                Назад
                              </Button>
                            </div>
                          </>
                        )}
                      </div>
                    )}
                  </div>
                )}
              </>
            ) : authTab === 'qr' ? (
              <TelegramQRAuth onSuccess={handleAuthSuccess} onCancel={handleAuthCancel} tenantId={effectiveTenantId} />
            ) : (
              // Phone auth уже отображается выше в phoneAuthStep
              null
            )}
          </CardContent>
        </Card>
      </div>
    </MainLayout>
  );
}

